import { createTheme, type Theme } from "@mui/material/styles";
import { buildCustomStyles } from "../assets/theme/custom/buildCustomStyles";

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) => v.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Darken or lighten a #RRGGBB color; amount in [-1, 1]. */
export function shiftHexColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const factor = 1 + amount;
  return rgbToHex(
    Math.round(clamp01(r / 255 * factor) * 255),
    Math.round(clamp01(g / 255 * factor) * 255),
    Math.round(clamp01(b / 255 * factor) * 255)
  );
}

function attachCustomStyles(theme: Theme): Theme {
  return {
    ...theme,
    custom: buildCustomStyles(theme),
  };
}

export function buildBrandedTheme(baseTheme: Theme, primaryColor?: string): Theme {
  if (!primaryColor || !HEX_COLOR.test(primaryColor)) {
    return attachCustomStyles(baseTheme);
  }

  const focus = shiftHexColor(primaryColor, -0.18);
  const state = shiftHexColor(primaryColor, -0.28);
  const baseAny = baseTheme as unknown as Record<string, unknown>;
  const palette = baseAny.palette as Record<string, unknown>;

  const theme = createTheme({
    ...baseTheme,
    palette: {
      ...palette,
      primary: {
        ...(palette.primary as object),
        main: primaryColor,
        dark: focus,
        light: shiftHexColor(primaryColor, 0.15),
      },
      gradients: palette.gradients
        ? {
            ...(palette.gradients as object),
            primary: { main: primaryColor, state },
          }
        : undefined,
    },
  } as Parameters<typeof createTheme>[0]);

  return attachCustomStyles(theme);
}
